/**
 * Audit trail event catalog.
 *
 * Pure TypeScript — NO JSX, so it is unit-testable in isolation. The drawer
 * calls describeAuditEntry() and renders the result; all display logic lives
 * here.
 *
 * Resolution order: explicit registry → field-update table → humanising
 * fallback.
 */

import type {
  ServiceAuditDetail,
  ServiceAuditSource,
  ServiceAuditTimeEntryMeta,
  ServiceAuditTrailEntry,
} from "shared";
// Imported from the module, NOT the "utilz" barrel — the barrel pulls in
// DOM-dependent modules that break this file's unit tests. Keep it direct.
import { dayjs } from "utilz/dayjs";
import { parseAuditSource } from "./parseAuditSource";

export type AuditIconKey =
  | "edit"
  | "user"
  | "schedule"
  | "tag"
  | "clock"
  | "checklist"
  | "link"
  | "unlink"
  | "merge"
  | "product"
  | "expense"
  | "invoice"
  | "feedback"
  | "device"
  | "flag"
  | "sla";

export interface AuditTrailView {
  /** Accordion header, e.g. "Board Updated". */
  title: string;
  /** Resolved to a component by auditIcons.tsx — keeps this file JSX-free. */
  icon: AuditIconKey;
  /** Bullet sentences rendered in the accordion body. Never empty. */
  lines: string[];
}

export interface AuditFormatOptions {
  serviceType: "ticket" | "project" | "task";
  /** User's preferred date format from the global store, e.g. "MM/DD/YYYY". */
  dateFormat: string;
  /** Resource timezone from the global store. */
  timezone: string;
}

const SOURCE_LABELS: Record<Exclude<ServiceAuditSource, "manual">, string> = {
  automation: "Automation",
  api: "API",
  sla: "SLA",
  system: "System",
};

/**
 * Who the sentence is attributed to. A non-manual `source` wins over the human
 * name, which is what makes "Automation updated status from …" work — an
 * automation-driven change should not be attributed to whoever's account the
 * rule happened to run under.
 *
 * The source is parsed rather than trusted: branching on `source !== "manual"`
 * alone committed to a label this map might not hold, and returned `undefined`
 * straight into the sentence when the service sent a value outside its own
 * enum. `parseAuditSource` degrades those to `"manual"` — which is exactly the
 * branch that reads the human name, so an unrecognised source now renders the
 * actor already sitting on the entry.
 */
export const actorLabel = (entry: ServiceAuditTrailEntry): string => {
  const source = parseAuditSource(entry.source);
  if (source !== "manual") {
    // `source` is narrowed to the non-manual union here, so this lookup is
    // total by construction rather than by hope.
    return SOURCE_LABELS[source];
  }
  // activity_by is entry-level and always populated in practice; the fallback
  // only narrows `name: string | null`.
  return entry.activity_by?.name ?? "Someone";
};

/** ["A"] → "A" · ["A","B"] → "A and B" · ["A","B","C"] → "A, B and C" */
export const joinNames = (names: string[]): string => {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

/**
 * Guards the observed backend data bugs: a stringified duration object, and
 * null / blank states. An unusable value drops its clause from the sentence
 * rather than printing garbage.
 */
const UNUSABLE_STATES = new Set(["[object object]", "null", "undefined"]);

export const isUsableState = (value?: string | null): value is string =>
  typeof value === "string" &&
  value.trim() !== "" &&
  !UNUSABLE_STATES.has(value.trim().toLowerCase());

/** "ticket_board_updated" + "ticket" → "board_updated" */
const stripServicePrefix = (auditType: string, serviceType: string): string =>
  auditType.startsWith(`${serviceType}_`)
    ? auditType.slice(serviceType.length + 1)
    : auditType;

/** "board" → "Board". Already-uppercase words such as "SLA" are left alone. */
const capitalise = (word: string): string =>
  word === word.toUpperCase()
    ? word
    : word.charAt(0).toUpperCase() + word.slice(1);

/** "foo_bar_updated" → "Foo Bar Updated" */
const toTitleCase = (value: string): string =>
  value.split("_").filter(Boolean).map(capitalise).join(" ");

/**
 * One line per detail. Returns [] for an empty details array so callers can
 * substitute an entry-level sentence — ticket_created ships `audit_details: []`
 * and must not render a blank accordion body.
 */
const buildLines = (
  details: ServiceAuditDetail[],
  build: (detail: ServiceAuditDetail) => string,
): string[] => details.map(build);

/**
 * "{actor} updated {label} from {prev} to {next}", degrading cleanly when
 * either state is missing or unusable.
 */
const fieldSentence = (
  actor: string,
  label: string,
  previous?: string | null,
  next?: string | null,
): string => {
  if (isUsableState(previous) && isUsableState(next)) {
    return `${actor} updated ${label} from ${previous} to ${next}`;
  }
  if (isUsableState(next)) {
    return `${actor} updated ${label} to ${next}`;
  }
  return `${actor} updated ${label}`;
};

// ---------------------------------------------------------------------------
// Tier 1 — table-driven field updates
// ---------------------------------------------------------------------------

/**
 * Every "{actor} updated {label} from {a} to {b}" event. A table, not 25
 * switch cases. Key = the audit_type with the service prefix and the
 * "_updated" suffix stripped.
 */
const FIELD_LABELS: Record<string, string> = {
  status: "status",
  board: "board",
  priority: "priority",
  sla: "SLA",
  sla_status: "SLA status",
  contract: "contract",
  summary: "summary",
  description: "description",
  customer: "customer",
  user: "user",
  site: "site",
  source: "source",
  location: "location",
  category: "category",
  subcategory: "subcategory",
  impact: "impact",
  urgency: "urgency",
  work_type: "work type",
  work_role: "billing role",
  owner: "owner",
  budgeted_hours: "budgeted hours",
  estimated_end_date: "estimated end date",
  billable: "billable",
  device: "device",
  contact_time: "contact time",
};

/**
 * Fields the PRD names with the service in front — "Ticket category",
 * "Ticket owner". The noun follows the timeline, so a project reads
 * "project category" rather than "ticket category".
 */
const SERVICE_PREFIXED_FIELDS = new Set(["category", "subcategory", "owner"]);

/** Fields whose value is free text or HTML — the PRD renders no from → to. */
const VALUE_SUPPRESSED_FIELDS = new Set(["summary", "description"]);

/** Fields whose states are ISO timestamps. */
const DATE_FIELDS = new Set(["estimated_end_date", "contact_time"]);

const FIELD_ICONS: Record<string, AuditIconKey> = {
  priority: "flag",
  impact: "flag",
  urgency: "flag",
  sla: "sla",
  sla_status: "sla",
  device: "device",
  budgeted_hours: "clock",
  estimated_end_date: "clock",
  contact_time: "clock",
  user: "user",
  owner: "user",
  customer: "user",
};

const formatBoolean = (value?: string | null): string | null | undefined => {
  if (!isUsableState(value)) return value;
  const normalised = value.trim().toLowerCase();
  if (["true", "yes", "1"].includes(normalised)) return "Yes";
  if (["false", "no", "0"].includes(normalised)) return "No";
  return value;
};

const formatDateState = (
  value: string | null | undefined,
  options: AuditFormatOptions,
): string | null | undefined => {
  if (!isUsableState(value)) return value;
  const parsed = dayjs(value).tz(options.timezone);
  return parsed.isValid() ? parsed.format(options.dateFormat) : value;
};

const describeFieldUpdate = (
  entry: ServiceAuditTrailEntry,
  field: string,
  label: string,
  actor: string,
  options: AuditFormatOptions,
): AuditTrailView => {
  // "ticket subcategory" → "Ticket Subcategory Updated"
  const title = `${label.split(" ").map(capitalise).join(" ")} Updated`;

  const icon: AuditIconKey = FIELD_ICONS[field] ?? "edit";

  // Free-text / HTML fields: state the change, never the value.
  if (VALUE_SUPPRESSED_FIELDS.has(field)) {
    return { title, icon, lines: [`${actor} updated ${label}`] };
  }

  const transform = (value?: string | null) => {
    if (field === "billable") return formatBoolean(value);
    if (DATE_FIELDS.has(field)) return formatDateState(value, options);
    return value;
  };

  const lines = buildLines(entry.audit_details ?? [], (detail) => {
    const previous = transform(detail.previous_state);
    const next = transform(detail.current_state);

    // The PRD writes this one without an actor — it is a system transition.
    if (field === "sla_status") {
      return isUsableState(previous) && isUsableState(next)
        ? `SLA status changed from ${previous} to ${next}`
        : `SLA status changed${isUsableState(next) ? ` to ${next}` : ""}`;
    }
    return fieldSentence(actor, label, previous, next);
  });

  return {
    title,
    icon,
    lines: lines.length ? lines : [`${actor} updated ${label}`],
  };
};

// ---------------------------------------------------------------------------
// Tier 2 — relational and aggregated events
// ---------------------------------------------------------------------------

interface EventContext {
  entry: ServiceAuditTrailEntry;
  details: ServiceAuditDetail[];
  actor: string;
  options: AuditFormatOptions;
}

interface AuditEventConfig {
  title: string | ((ctx: EventContext) => string);
  icon: AuditIconKey;
  lines: (ctx: EventContext) => string[];
}

/**
 * The service noun inside a sentence — "ticket" | "project" | "task". This
 * drawer serves all three (ChatHeader passes "ticket", ProjectHeaderSection
 * passes "project"), so no copy may hardcode "ticket".
 */
const serviceNoun = (ctx: EventContext): string => ctx.options.serviceType;

/** The service noun in a title: "project" → "Project". */
const serviceTitle = (ctx: EventContext): string =>
  capitalise(ctx.options.serviceType);

/**
 * Best available human label for a detail: actor name, then the structured
 * item name, then a state value.
 *
 * `meta_data.line_item.name` earns its place in the ladder because relational
 * events pointing at a record that is not a resource — a KB article, a product
 * line — send an `activity_for` that is only an id, and put the readable name
 * here instead.
 *
 * It sits BELOW the states deliberately. `line_item` is also carried by the
 * product / expense / checklist events, which have always labelled themselves
 * from `current_state`; putting it above would silently change where those get
 * their label from, and nothing has confirmed the two agree. Below the states,
 * it only reaches details that had no label at all — which is the case it was
 * added for.
 */
const detailLabel = (detail: ServiceAuditDetail): string | null => {
  if (detail.activity_for?.name) return detail.activity_for.name;
  if (isUsableState(detail.current_state)) return detail.current_state;
  if (isUsableState(detail.previous_state)) return detail.previous_state;
  const itemName = detail.meta_data?.line_item?.name;
  if (isUsableState(itemName)) return itemName;
  return null;
};

const detailLabels = (details: ServiceAuditDetail[]): string[] =>
  details.map(detailLabel).filter((label): label is string => label !== null);

/** Aggregates every detail into ONE sentence: "…User2, User3 and User4…". */
const aggregated =
  (build: (names: string, count: number, ctx: EventContext) => string) =>
  (ctx: EventContext): string[] => {
    const names = detailLabels(ctx.details);
    if (names.length === 0) return [];
    return [build(joinNames(names), names.length, ctx)];
  };

/**
 * "45 minutes" / "1 hour 45 minutes" from a time entry's start and end.
 *
 * Returns null unless BOTH ends are present and parseable, and the span is
 * positive — a half-populated pair would otherwise render "0 minutes", which
 * reads as a logged zero rather than as missing data. The caller falls back to
 * a shorter sentence when this is null.
 */
const durationPhrase = (
  meta?: ServiceAuditTimeEntryMeta | null,
): string | null => {
  const start = meta?.start_time
    ? new Date(meta.start_time).getTime()
    : Number.NaN;
  const end = meta?.end_time ? new Date(meta.end_time).getTime() : Number.NaN;
  if (Number.isNaN(start) || Number.isNaN(end)) return null;

  const totalMinutes = Math.round((end - start) / 60_000);
  if (totalMinutes <= 0) return null;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  if (minutes > 0)
    parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
  return parts.join(" ");
};

/**
 * One time-entry sentence, in the most informative form the detail supports.
 *
 * These events carry no `activity_for` and, per the service, no states — it all
 * sits under `meta_data.time_entry`. Before this the generic `perDetail` path
 * produced no line at all and the entry fell through to "X updated the ticket".
 *
 * The ladder exists because older entries predate `meta_data` and carry
 * pre-formatted durations in the state fields; ignoring those would make old
 * rows read WORSE than they do today.
 *
 *   1. meta_data      → "added a time entry of 45 minutes"
 *   2. both states    → "updated time entry from 1h to 1h 30m"  (richest)
 *   3. current_state  → "added a time entry of 1h 30m"
 *   4. nothing usable → "added a time entry"
 */
const timeEntryLine = (
  detail: ServiceAuditDetail,
  actor: string,
  verb: "added" | "updated",
): string => {
  const duration = durationPhrase(detail.meta_data?.time_entry);

  if (
    !duration &&
    isUsableState(detail.previous_state) &&
    isUsableState(detail.current_state)
  ) {
    return `${actor} ${verb} time entry from ${detail.previous_state} to ${detail.current_state}`;
  }

  const span =
    duration ??
    (isUsableState(detail.current_state) ? detail.current_state : null);
  if (!span) return `${actor} ${verb} a time entry`;

  // "billable" only when explicitly true — the field is optional, and
  // `undefined` must not be reported as non-billable.
  const suffix =
    detail.meta_data?.time_entry?.is_billable === true ? " (billable)" : "";
  // Duration AFTER the noun: "a 45 minutes time entry" is wrong (an attributive
  // unit takes the singular) and "a 45 minute time entry" then breaks on
  // "1 hour 45 minute". This phrasing sidesteps both.
  return `${actor} ${verb} a time entry of ${span}${suffix}`;
};

/** `time_entry_added` / `time_entry_updated` — one sentence per detail. */
const timeEntrySentence =
  (verb: "added" | "updated") =>
  (ctx: EventContext): string[] =>
    ctx.details.length
      ? ctx.details.map((detail) => timeEntryLine(detail, ctx.actor, verb))
      : [`${ctx.actor} ${verb} a time entry`];

/**
 * One sentence per detail.
 *
 * `buildPlain` is the sentence to use when a detail carries no readable label.
 * It matters for the link events: `kb_article_linked` sends an `activity_for`
 * that is nothing but an id, and `kb_article_removed` sends `name: null`.
 * Without a plain form those details drop out, the line list comes back empty,
 * and `describeCatalogEvent` falls through to "X updated the ticket" — losing
 * the verb, which is the only thing the row was there to say.
 *
 * It also covers an empty `details` array, for the same reason.
 *
 * Events that pass no `buildPlain` keep the previous behaviour: an unlabelled
 * detail is skipped, because their sentence makes no sense without its subject.
 */
const perDetail =
  (
    build: (
      label: string,
      detail: ServiceAuditDetail,
      ctx: EventContext,
    ) => string,
    buildPlain?: (ctx: EventContext) => string,
  ) =>
  (ctx: EventContext): string[] => {
    if (ctx.details.length === 0) {
      return buildPlain ? [buildPlain(ctx)] : [];
    }
    return ctx.details
      .map((detail) => {
        const label = detailLabel(detail);
        if (label !== null) return build(label, detail, ctx);
        return buildPlain ? buildPlain(ctx) : null;
      })
      .filter((line): line is string => line !== null);
  };

/** A sentence with no per-detail data — timer, lifecycle, etc. */
const standalone =
  (build: (actor: string, ctx: EventContext) => string) =>
  (ctx: EventContext): string[] => [build(ctx.actor, ctx)];

const plural = (single: string, many: string) => (ctx: EventContext) =>
  ctx.details.length > 1 ? many : single;

/** Schedule range, e.g. "09:00 AM to 11:00 AM" — omitted when absent. */
const scheduleRange = (detail: ServiceAuditDetail): string =>
  isUsableState(detail.current_state) ? ` from ${detail.current_state}` : "";

const EVENT_CATALOG: Record<string, AuditEventConfig> = {
  // --- lifecycle -----------------------------------------------------------
  created: {
    title: (ctx) => `${serviceTitle(ctx)} Created`,
    icon: "edit",
    lines: standalone((actor, ctx) => `${actor} created ${serviceNoun(ctx)}`),
  },
  reopened: {
    title: (ctx) => `${serviceTitle(ctx)} Reopened`,
    icon: "edit",
    lines: standalone(
      (actor, ctx) => `${actor} reopened the ${serviceNoun(ctx)}`,
    ),
  },
  closed: {
    title: (ctx) => `${serviceTitle(ctx)} Closed`,
    icon: "edit",
    lines: standalone(
      (actor, ctx) => `${actor} closed the ${serviceNoun(ctx)}`,
    ),
  },
  cancelled: {
    title: (ctx) => `${serviceTitle(ctx)} Cancelled`,
    icon: "edit",
    lines: standalone(
      (actor, ctx) => `${actor} cancelled the ${serviceNoun(ctx)}`,
    ),
  },
  merged: {
    title: (ctx) => `${serviceTitle(ctx)} Merged`,
    icon: "merge",
    lines: perDetail(
      (label, _detail, ctx) =>
        `${ctx.actor} merged ${label} into this ${serviceNoun(ctx)}`,
    ),
  },
  escalated: {
    title: (ctx) => `${serviceTitle(ctx)} Escalated`,
    icon: "user",
    lines: perDetail(
      (label, _detail, ctx) =>
        `${ctx.actor} escalated the ${serviceNoun(ctx)} to ${label}`,
    ),
  },
  // A distinct audit_type rather than `escalated` + source:"sla", so the actor
  // is always the literal "SLA" — activity_by is ignored here by design.
  escalated_by_sla: {
    title: (ctx) => `${serviceTitle(ctx)} Escalated`,
    icon: "user",
    lines: perDetail(
      (label, _detail, ctx) =>
        `SLA escalated the ${serviceNoun(ctx)} to ${label}`,
    ),
  },
  manually_invoiced: {
    title: "Manually Invoiced",
    icon: "invoice",
    lines: standalone(
      (actor, ctx) => `${actor} manually invoiced the ${serviceNoun(ctx)}`,
    ),
  },
  // TODO(copy): the enum ships ticket_invoice_updated but the PRD gives no
  // display text for it — confirm the wording with the Billing service owner.
  invoice_updated: {
    title: "Invoice Updated",
    icon: "invoice",
    lines: standalone(
      (actor, ctx) =>
        `${actor} updated the invoice for this ${serviceNoun(ctx)}`,
    ),
  },
  feedback_requested: {
    title: "Feedback Requested",
    icon: "feedback",
    lines: standalone((actor) => `${actor} requested feedback`),
  },

  // --- assignees -----------------------------------------------------------
  assignee_added: {
    title: plural("Assignee Added", "Multiple Assignees Added"),
    icon: "user",
    lines: aggregated(
      (names, _count, ctx) =>
        `${ctx.actor} assigned ${names} to the ${serviceNoun(ctx)}`,
    ),
  },
  assignee_removed: {
    title: plural("Assignee Removed", "Multiple Assignees Removed"),
    icon: "user",
    lines: aggregated(
      (names, count, ctx) =>
        `${ctx.actor} removed ${names} as ${
          count > 1 ? "assignees" : "assignee"
        } from the ${serviceNoun(ctx)}`,
    ),
  },
  // Backend emits these as their OWN audit types rather than sending several
  // details on assignee_added, so plurality is not inferred from the payload.
  multiple_assignees_added: {
    title: "Multiple Assignees Added",
    icon: "user",
    lines: aggregated(
      (names, _count, ctx) =>
        `${ctx.actor} assigned ${names} to the ${serviceNoun(ctx)}`,
    ),
  },
  multiple_assignees_removed: {
    title: "Multiple Assignees Removed",
    icon: "user",
    lines: aggregated(
      (names, _count, ctx) =>
        `${ctx.actor} removed ${names} as assignees from the ${serviceNoun(ctx)}`,
    ),
  },
  assignee_and_schedule_added: {
    title: "Assignee And Schedule Added",
    icon: "schedule",
    lines: perDetail(
      (label, detail, ctx) =>
        `${ctx.actor} assigned ${label} to the ${serviceNoun(
          ctx,
        )} and added schedule${scheduleRange(detail)}`,
    ),
  },

  // --- schedules -----------------------------------------------------------
  schedule_added: {
    title: plural("Schedule Added", "Multiple Schedules Added"),
    icon: "schedule",
    lines: perDetail(
      (label, detail, { actor }) =>
        `${actor} added schedule for ${label}${scheduleRange(detail)}`,
    ),
  },
  schedule_updated: {
    title: "Schedule Updated",
    icon: "schedule",
    lines: perDetail((label, detail, { actor }) =>
      isUsableState(detail.previous_state) &&
      isUsableState(detail.current_state)
        ? `${actor} updated schedule for ${label} from ${detail.previous_state} to ${detail.current_state}`
        : `${actor} updated schedule for ${label}`,
    ),
  },
  schedule_removed: {
    title: plural("Schedule Removed", "Multiple Schedules Removed"),
    icon: "schedule",
    lines: perDetail(
      (label, detail, { actor }) =>
        `${actor} removed schedule for ${label}${scheduleRange(detail)}`,
    ),
  },

  // --- tags ----------------------------------------------------------------
  tags_added: {
    title: "Tags Added",
    icon: "tag",
    lines: aggregated(
      (names, _count, ctx) =>
        `${ctx.actor} added ${names} to the ${serviceNoun(ctx)}`,
    ),
  },
  tags_removed: {
    title: "Tags Removed",
    icon: "tag",
    lines: aggregated(
      (names, _count, ctx) =>
        `${ctx.actor} removed ${names} from the ${serviceNoun(ctx)}`,
    ),
  },

  // --- participants --------------------------------------------------------
  participant_added: {
    title: plural("Participant Added", "Multiple Participants Added"),
    icon: "user",
    lines: aggregated(
      (names, _count, ctx) => `${ctx.actor} added ${names} to the conversation`,
    ),
  },
  participant_removed: {
    title: plural("Participant Removed", "Multiple Participants Removed"),
    icon: "user",
    lines: aggregated(
      (names, _count, ctx) =>
        `${ctx.actor} removed ${names} from the conversation`,
    ),
  },
  multiple_participants_added: {
    title: "Multiple Participants Added",
    icon: "user",
    lines: aggregated(
      (names, _count, ctx) => `${ctx.actor} added ${names} to the conversation`,
    ),
  },
  multiple_participants_removed: {
    title: "Multiple Participants Removed",
    icon: "user",
    lines: aggregated(
      (names, _count, ctx) =>
        `${ctx.actor} removed ${names} from the conversation`,
    ),
  },
  external_participant_added: {
    title: "External Participant Added",
    icon: "user",
    lines: aggregated(
      (names, _count, ctx) => `${ctx.actor} added ${names} to the conversation`,
    ),
  },
  external_participant_removed: {
    title: "External Participant Removed",
    icon: "user",
    lines: aggregated(
      (names, _count, ctx) =>
        `${ctx.actor} removed ${names} from the conversation`,
    ),
  },
  participant_joined: {
    title: "Participant Joined",
    icon: "user",
    lines: standalone((actor) => `${actor} joined the conversation`),
  },
  participant_added_from_email_cc: {
    title: "Participant Added From Email CC",
    icon: "user",
    lines: perDetail(
      (label) => `${label} was added to the conversation from email CC`,
    ),
  },
  external_participant_added_from_email_cc: {
    title: "External Participant Added From Email CC",
    icon: "user",
    lines: perDetail(
      (label) => `${label} was added to the conversation from email CC`,
    ),
  },

  // --- time ----------------------------------------------------------------
  timer_started: {
    title: "Timer Started",
    icon: "clock",
    lines: standalone((actor) => `${actor} started the timer`),
  },
  timer_stopped: {
    title: "Timer Stopped",
    icon: "clock",
    lines: standalone((actor) => `${actor} stopped the timer`),
  },
  time_entry_added: {
    title: "Time Entry Created",
    icon: "clock",
    lines: timeEntrySentence("added"),
  },
  time_entry_updated: {
    title: "Time Entry Updated",
    icon: "clock",
    lines: timeEntrySentence("updated"),
  },
  time_entry_removed: {
    title: "Time Entry Removed",
    icon: "clock",
    lines: perDetail(
      (label, _detail, { actor }) =>
        `${actor} removed a time entry of ${label}`,
    ),
  },
  contact_time_added: {
    title: "Contact Time Added",
    icon: "clock",
    lines: perDetail(
      (label, _detail, { actor }) => `${actor} added contact time ${label}`,
    ),
  },

  // --- checklist / products / expenses -------------------------------------
  checklist_item_added: {
    title: "Checklist Added",
    icon: "checklist",
    lines: perDetail(
      (label, _detail, { actor }) => `${actor} added checklist ${label}`,
    ),
  },
  checklist_item_removed: {
    title: "Checklist Removed",
    icon: "checklist",
    lines: perDetail(
      (label, _detail, { actor }) => `${actor} removed checklist ${label}`,
    ),
  },
  product_added: {
    title: "Product Added",
    icon: "product",
    lines: perDetail(
      (label, _detail, ctx) =>
        `${ctx.actor} added ${label} to the ${serviceNoun(ctx)}`,
    ),
  },
  product_removed: {
    title: "Product Removed",
    icon: "product",
    lines: perDetail(
      (label, _detail, ctx) =>
        `${ctx.actor} removed ${label} from the ${serviceNoun(ctx)}`,
    ),
  },
  expense_added: {
    title: "Expense Added",
    icon: "expense",
    lines: perDetail(
      (label, _detail, ctx) =>
        `${ctx.actor} added ${label} to the ${serviceNoun(ctx)}`,
    ),
  },
  expense_removed: {
    title: "Expense Removed",
    icon: "expense",
    lines: perDetail(
      (label, _detail, ctx) =>
        `${ctx.actor} removed ${label} from the ${serviceNoun(ctx)}`,
    ),
  },

  // --- links ---------------------------------------------------------------
  child_ticket_linked: {
    title: "Child Ticket Linked",
    icon: "link",
    lines: perDetail(
      (label, _detail, { actor }) => `${actor} linked child ticket ${label}`,
      ({ actor }) => `${actor} linked a child ticket`,
    ),
  },
  child_ticket_removed: {
    title: "Child Ticket Removed",
    icon: "unlink",
    lines: perDetail(
      (label, _detail, { actor }) => `${actor} unlinked child ticket ${label}`,
      ({ actor }) => `${actor} unlinked a child ticket`,
    ),
  },
  // The service sends no article title today: `kb_article_linked` ships
  // `activity_for: { id }` with no name, `kb_article_removed` ships
  // `name: null`. The plain sentence is what these render until it does — an
  // article id is not a name, so it is not shown in its place.
  kb_article_linked: {
    title: "Knowledge Base Article Linked",
    icon: "link",
    lines: perDetail(
      (label, _detail, { actor }) =>
        `${actor} linked knowledge base article ${label}`,
      ({ actor }) => `${actor} linked a knowledge base article`,
    ),
  },
  kb_article_removed: {
    title: "Knowledge Base Article Unlinked",
    icon: "unlink",
    lines: perDetail(
      (label, _detail, { actor }) =>
        `${actor} unlinked knowledge base article ${label}`,
      ({ actor }) => `${actor} unlinked a knowledge base article`,
    ),
  },
};

/**
 * Status values that ARE a lifecycle event rather than a field edit.
 *
 * The service has `ticket_cancelled` in its 90-type enum but does not emit it:
 * cancelling a ticket arrives as `ticket_status_updated` with
 * `current_state: "Cancelled"` (live UAT, 2026-09-03). The PRD's row for that
 * action is "User1 cancelled the ticket", so the status update is redirected to
 * the lifecycle event it actually is — reusing that event's own copy rather
 * than restating it here, so the two can never disagree.
 *
 * Keyed by the default status LABEL, because that is what an audit state
 * carries. `statusData` holds uuids and those never appear in `current_state`;
 * `AllDefaultStatus` in shared/data.ts is the label set this mirrors.
 *
 * `Closed` and `Reopened` are deliberately absent: it is unconfirmed whether
 * the service also emits `ticket_closed` / `ticket_reopened` alongside the
 * status update, and redirecting when it does would render the row twice. Once
 * that is confirmed, each is one line here.
 *
 * The value type is the three lifecycle keys and NOT `string`, because only a
 * `standalone` event is safe to redirect to. `Merged` and `Escalated` are also
 * default statuses with same-named catalog entries, but those are `perDetail`
 * configs that read the detail for a subject — redirecting `Merged` would
 * render "X merged Merged into this ticket". The union makes that a compile
 * error at the point of the mistake instead of a shipped sentence.
 */
const LIFECYCLE_STATUS_EVENTS: Partial<
  Record<string, "cancelled" | "closed" | "reopened">
> = {
  cancelled: "cancelled",
};

/**
 * The lifecycle event a status change really is, or null to leave it as a
 * normal "updated status from A to B" row.
 *
 * Only a single-detail entry redirects — a batched status entry has no one
 * lifecycle to name, and picking a detail arbitrarily would drop the others.
 */
const lifecycleEventForStatus = (
  entry: ServiceAuditTrailEntry,
): AuditEventConfig | null => {
  const details = entry.audit_details ?? [];
  if (details.length !== 1) return null;

  const next = details[0].current_state;
  if (!isUsableState(next)) return null;

  const key = LIFECYCLE_STATUS_EVENTS[next.trim().toLowerCase()];
  return key ? (EVENT_CATALOG[key] ?? null) : null;
};

const describeCatalogEvent = (
  config: AuditEventConfig,
  ctx: EventContext,
): AuditTrailView => {
  const lines = config.lines(ctx);
  const title =
    typeof config.title === "function" ? config.title(ctx) : config.title;

  return {
    title,
    icon: config.icon,
    // A relational event whose details were all unlabelled still needs a line.
    lines: lines.length
      ? lines
      : [`${ctx.actor} updated the ${serviceNoun(ctx)}`],
  };
};

/** Tier 3 — an event no tier recognised. Reads sensibly on day one. */
const describeUnknown = (
  entry: ServiceAuditTrailEntry,
  key: string,
  actor: string,
): AuditTrailView => {
  const isUpdate = key.endsWith("_updated");
  const subject = (isUpdate ? key.slice(0, -"_updated".length) : key).replace(
    /_/g,
    " ",
  );

  const lines = isUpdate
    ? buildLines(entry.audit_details ?? [], (detail) =>
        fieldSentence(
          actor,
          subject,
          detail.previous_state,
          detail.current_state,
        ),
      )
    : [];

  return {
    title: toTitleCase(key),
    icon: "edit",
    lines: lines.length
      ? lines
      : [
          isUpdate
            ? `${actor} updated ${subject}`
            : `${actor} performed ${subject}`,
        ],
  };
};

type AuditResolution =
  | { tier: "event"; config: AuditEventConfig }
  | { tier: "field"; field: string; label: string }
  | { tier: "fallback"; key: string };

/**
 * Single source of truth for which tier handles an audit_type. describeAuditEntry
 * and isKnownAuditType both go through this so they cannot drift apart.
 */
const resolveAuditType = (
  auditType: string,
  serviceType: AuditFormatOptions["serviceType"],
): AuditResolution => {
  const key = stripServicePrefix(auditType, serviceType);

  // Tier 2 — explicit registry. Tried on the stripped key first, then the raw
  // key, because relational events ship without a service prefix while field
  // events carry one.
  const config = EVENT_CATALOG[key] ?? EVENT_CATALOG[auditType];
  if (config) return { tier: "event", config };

  // Tier 1 — table-driven field updates.
  if (key.endsWith("_updated")) {
    const field = key.slice(0, -"_updated".length);
    const label = FIELD_LABELS[field];
    if (label) {
      return {
        tier: "field",
        field,
        label: SERVICE_PREFIXED_FIELDS.has(field)
          ? `${serviceType} ${label}`
          : label,
      };
    }
  }

  return { tier: "fallback", key };
};

/**
 * True when the audit type has explicit agreed copy rather than the humanising
 * fallback. Exported so a test can pin coverage of the backend's audit_type
 * enum — the fallback is deliberately indistinguishable from real copy at the
 * output level, so coverage cannot be asserted by inspecting the strings.
 */
export const isKnownAuditType = (
  auditType: string,
  serviceType: AuditFormatOptions["serviceType"] = "ticket",
): boolean => resolveAuditType(auditType, serviceType).tier !== "fallback";

export const describeAuditEntry = (
  entry: ServiceAuditTrailEntry,
  options: AuditFormatOptions,
): AuditTrailView => {
  const actor = actorLabel(entry);
  const resolution = resolveAuditType(entry.audit_type, options.serviceType);

  switch (resolution.tier) {
    case "event":
      return describeCatalogEvent(resolution.config, {
        entry,
        details: entry.audit_details ?? [],
        actor,
        options,
      });
    case "field": {
      // A status change that is really a lifecycle action reads as that action
      // — see LIFECYCLE_STATUS_EVENTS.
      if (resolution.field === "status") {
        const lifecycle = lifecycleEventForStatus(entry);
        if (lifecycle) {
          return describeCatalogEvent(lifecycle, {
            entry,
            details: entry.audit_details ?? [],
            actor,
            options,
          });
        }
      }
      return describeFieldUpdate(
        entry,
        resolution.field,
        resolution.label,
        actor,
        options,
      );
    }
    default:
      return describeUnknown(entry, resolution.key, actor);
  }
};
